import React, { useState, useEffect } from 'react';
import SourceCitations from './SourceCitations';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { fetchPdfBinary, fetchPdfInfo } from '../../utils/chatApi';

// Add a utility function to highlight text in double quotes
const highlightQuotedText = (text: string) => {
  // Only process strings
  if (typeof text !== 'string') return text;

  // Regular expression to match text inside double quotes
  const regex = /"([^"]+)"/g;

  // Split the text by double quotes and wrap quoted parts in spans
  const parts = text.split(regex);

  if (parts.length <= 1) return text;

  const result = [];
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      // Non-quoted text
      result.push(parts[i]);
    } else {
      // Quoted text - wrap in span
      result.push(<span key={i} className="quoted-text">{parts[i]}</span>);
    }
  }

  return result;
};

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  sources?: {
    id: string;
    title: string;
    page: number;
    content: string;
    type: 'pdf' | 'doc' | 'text' | string;
    url?: string|Promise<string|null>;
  }[];
  files?: {
    name: string;
    type: string;
    size: number;
    url?: string;
  }[];
}

interface ChatMessageProps {
  message: Message;
  onRegenerate?: (messageId: string) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onRegenerate }) => {
  console.log("This is the message", message);
  const [processedSources, setProcessedSources] = useState<any[]>([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);

  // Handle the PDF URL fetching
  useEffect(() => {
    const loadSources = async () => {
      if (!message.sources || message.sources.length === 0) {
        setProcessedSources([]);
        setSourcesLoaded(true);
        return;
      }

      const updatedSources = await Promise.all(
        message.sources.map(async (source) => {
          const newSource = { ...source };

          // Fetch PDF info to get title and type
          const pdfInfo = await fetchPdfInfo(source.id);
          if (pdfInfo) {
            newSource.title = pdfInfo.title || source.title;
            newSource.type = pdfInfo.type;
          } else {
            // Fallback to 'pdf' if info can't be fetched
            newSource.type = 'pdf';
          }

          if (!source.url) {
            try {
              const resolvedUrl = await fetchPdfBinary(source.id);
              newSource.url = resolvedUrl as string;
            } catch (error) {
              console.error("Error fetching PDF binary:", error);
              newSource.url = undefined;
            }
          } else if (source.url instanceof Promise) {
            try {
              const resolvedUrl = await source.url;
              newSource.url = resolvedUrl as string;
            } catch (error) {
              console.error("Error resolving URL promise:", error);
              newSource.url = undefined;
            }
          }
          return newSource;
        })
      );

      setProcessedSources(updatedSources);
      setSourcesLoaded(true);
    };

    loadSources();
  }, [message.sources]);

  const isUser = message.role === 'user';
  const hasSources = !isUser && message.sources && message.sources.length > 0;
  const [isLiked, setIsLiked] = useState<boolean | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Add debugging for sources
  if (!isUser && message.sources) {
    console.log(`ChatMessage: Message has ${message.sources.length} sources:`, message.sources);
  }

  // Function to handle the error message translation
  const getDisplayContent = () => {
    if (!isUser && message.content === "Agent stopped due to max iterations.") {
      return "Apologies, it seems I don't have enough details to provide an accurate response. To avoid any guesswork, could you share a bit more about your question? I'll get back to you with a clear and precise answer as soon as possible!";
    }
    return message.content;
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(message.content)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  const handleLike = () => {
    setIsLiked(true);
  };

  const handleDislike = () => {
    setIsLiked(false);
  };

  const handleRegenerate = () => {
    // Call the onRegenerate prop function if it exists
    if (onRegenerate) {
      onRegenerate(message.id);
    } else {
      // Fallback for dummy implementation
      console.log('Regenerate response requested for message:', message.id);
    }
  };

  return (
    <div className={`chat-message`}>
      <div className="max-w-5xl mx-auto flex gap-4">

        {/* Avatar */}
        <div className="flex-shrink-0">
          {isUser ? (
            <div className="h-10 w-10 rounded-xl bg-gray-600 flex items-center justify-center shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-white">
                <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
              </svg>
            </div>
          ) : (
            <div className="h-10 w-10 rounded-xl bg-gray-600 flex items-center justify-center shadow-md">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-white">
                <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
                <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
              </svg>
            </div>
          )}
        </div>

        {/* Message content */}
        <div className="flex-1">
          <div className="font-medium text-white mb-2 flex items-center">
            {isUser ? 'You' : (
              <span className="text-gray-300 font-bold">
                Agent GG
              </span>
            )}
            <span className="ml-2 text-xs text-gray-400">
              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className={`${isUser ? 'bg-gray-700' : 'bg-gray-600'} rounded-xl p-4 shadow-md text-gray-100 markdown-content`}>
            {/* Display file attachments if present */}
            {isUser && message.files && message.files.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {message.files.map((file, index) => (
                  <div key={index} className="inline-flex items-center p-2 bg-gray-800 rounded-md">
                    <div className="flex items-center">
                      {file.type.startsWith('image/') ? (
                        <div className="h-12 w-12 flex items-center justify-center bg-gray-700 rounded-md mr-2">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-blue-400">
                            <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      ) : file.type === 'application/pdf' ? (
                        <div className="h-12 w-12 flex items-center justify-center bg-gray-700 rounded-md mr-2">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-red-400">
                            <path d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625z" />
                            <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
                          </svg>
                        </div>
                      ) : (
                        <div className="h-12 w-12 flex items-center justify-center bg-gray-700 rounded-md mr-2">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-gray-300">
                            <path fillRule="evenodd" d="M5.625 1.5H9a3.75 3.75 0 013.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 013.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 01-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875zm5.845 17.03a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V12a.75.75 0 00-1.5 0v4.19l-1.72-1.72a.75.75 0 00-1.06 1.06l3 3z" clipRule="evenodd" />
                            <path d="M14.25 5.25a5.23 5.23 0 00-1.279-3.434 9.768 9.768 0 016.963 6.963A5.23 5.23 0 0016.5 7.5h-1.875a.375.375 0 01-.375-.375V5.25z" />
                          </svg>
                        </div>
                      )}
                      <div className="text-sm text-white truncate max-w-xs">
                        {file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isUser ? (
              <div className="whitespace-pre-wrap leading-relaxed">{message.content}</div>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  // Add custom styling for markdown elements
                  p: ({node, ...props}) => {
                    // Apply quoted text highlighting to paragraph content
                    const children = highlightQuotedText(props.children as string);
                    return <p className="my-1" {...props}>{children}</p>;
                  },
                  h1: ({node, ...props}) => <h1 className="text-xl font-bold my-2" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-lg font-bold my-2" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-md font-bold my-1" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-5 my-2" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-5 my-2" {...props} />,
                  li: ({node, ...props}) => {
                    // Apply quoted text highlighting to list items
                    const children = highlightQuotedText(props.children as string);
                    return <li className="my-1" {...props}>{children}</li>;
                  },
                  a: ({node, ...props}) => <a className="text-blue-400 hover:underline" {...props} />,
                  // Add handling for table cells
                  td: ({node, ...props}) => {
                    // Apply quoted text highlighting to table cells
                    const children = highlightQuotedText(props.children as string);
                    return <td {...props}>{children}</td>;
                  },
                  th: ({node, ...props}) => <th {...props}>{props.children}</th>,
                  code: ({node, className, children, ...props}: any) => {
                    const match = /language-(\w+)/.exec(className || '');
                    const isInline = !className || !match;

                    return isInline ? (
                      <code className="bg-gray-800 px-1 rounded text-sm font-mono" {...props}>
                        {children}
                      </code>
                    ) : (
                      <div className="code-block-wrapper">
                        {match && (
                          <div className="code-language-tag text-xs text-gray-400 px-4 py-1 bg-gray-800 rounded-t-md font-mono">
                            {match[1]}
                          </div>
                        )}
                        <pre className={className}>
                          <code className={className} {...props}>
                            {children}
                          </code>
                        </pre>
                      </div>
                    );
                  }
                }}
              >
                {getDisplayContent()}
              </ReactMarkdown>
            )}

            {/* Source citations */}
            {hasSources && !sourcesLoaded && (
              <div className="mt-4">
                <p className="text-sm text-gray-400 mb-2">Loading sources...</p>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: '300ms' }}></div>
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: '600ms' }}></div>
                </div>
              </div>
            )}
            {hasSources && sourcesLoaded && <SourceCitations sources={processedSources} />}
          </div>

          {/* Message actions */}
          {!isUser && (
            <div className="flex mt-4 space-x-3">
              {/* Like button */}
              <button
                onClick={handleLike}
                className={`p-2 ${isLiked === true ? 'text-green-400 bg-gray-700' : 'text-gray-400'} hover:text-green-400 hover:bg-gray-700 rounded-lg transition-colors`}
                aria-label="Like"
                title="Like"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill={isLiked === true ? "currentColor" : "none"} viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" />
                </svg>
              </button>

              {/* Dislike button */}
              <button
                onClick={handleDislike}
                className={`p-2 ${isLiked === false ? 'text-red-400 bg-gray-700' : 'text-gray-400'} hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors`}
                aria-label="Dislike"
                title="Dislike"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill={isLiked === false ? "currentColor" : "none"} viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 15h2.25m8.024-9.75c.011.05.028.1.052.148.591 1.2.924 2.55.924 3.977a8.96 8.96 0 01-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398C20.613 14.547 19.833 15 19 15h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 00.303-.54m.023-8.25H16.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.504c-.618 0-1.217.247-1.605.729A11.95 11.95 0 002.25 12c0 .434.023.863.068 1.285C2.427 14.306 3.346 15 4.372 15h3.126c.618 0 .991.724.725 1.282A7.471 7.471 0 007.5 19.5a2.25 2.25 0 002.25 2.25.75.75 0 00.75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 002.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384" />
                </svg>
              </button>

              {/* Regenerate button */}
              <button
                onClick={handleRegenerate}
                className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Regenerate response"
                title="Regenerate response"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </button>

              {/* Copy button */}
              <button
                onClick={handleCopyText}
                className={`p-2 ${copySuccess ? 'text-green-400 bg-gray-700' : 'text-gray-400'} hover:text-gray-200 hover:bg-gray-700 rounded-lg transition-colors`}
                aria-label="Copy text"
                title="Copy text"
              >
                {copySuccess ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
